import { useUserStore } from '~/store/user';
import type { ProductModel } from '#models/product';
import type { ProductRaw } from '#constants/types/algolia';

/**
 * Composable for checking product purchase eligibility
 *
 * Products with isDisplayOnly:true are displayed even if not on user's pricelist,
 * but the user can only purchase if the product is on one of their pricelists.
 */
export default function useProductPurchase() {
  const userStore = useUserStore();

  /**
   * Get raw product data (handles both ProductModel and ProductRaw)
   */
  const getRawData = (product: ProductModel | ProductRaw): ProductRaw => {
    return ('_ProductRaw' in product)
      ? (product as ProductModel)._ProductRaw
      : product as ProductRaw;
  };

  /**
   * Check if user has this product on their pricelist
   */
  const isOnUserPricelist = (product: ProductModel | ProductRaw): boolean => {
    if (!userStore.loggedIn) {
      return false;
    }

    const userPricelistIds = userStore.userProfile.pricelistIds ?? [];
    if (userPricelistIds.length === 0) {
      return false;
    }

    const raw = getRawData(product);
    const productPricelists = raw.availableOnPricelists;

    if (!productPricelists || productPricelists.length === 0) {
      // No specific pricelist restrictions
      return raw.isPublic;
    }

    return userPricelistIds.some(id => productPricelists.includes(id));
  };

  /**
   * Check if the user can purchase a product
   *
   * Logic:
   * - User's pricelist price (if available) → can purchase
   * - DisplayOnly product without user pricelist → cannot purchase
   * - Standard public product → can purchase
   */
  const canPurchase = (product: ProductModel | ProductRaw): boolean => {
    if (!userStore.loggedIn) {
      return false;
    }

    if (userStore.isMediabankUser) {
      return false;
    }

    // If user has product on their pricelist, they can purchase
    if (isOnUserPricelist(product)) {
      return true;
    }

    // If it's a DisplayOnly product and user doesn't have it on pricelist, cannot purchase
    const raw = getRawData(product);
    if (raw.isDisplayOnly) {
      return false;
    }

    // Standard public product without specific pricelist restrictions
    return raw.isPublic;
  };

  /**
   * Check if this is a displayOnly product that user doesn't have on their pricelist
   * Used to hide price for these products
   */
  const shouldShowDisplayOnlyPrice = (product: ProductModel | ProductRaw): boolean => {
    // Not logged in - don't hide price
    if (!userStore.loggedIn) {
      return false;
    }

    const raw = getRawData(product);

    // Not a displayOnly product
    if (!raw.isDisplayOnly) {
      return false;
    }

    // User has product on their pricelist - show their price
    if (isOnUserPricelist(product)) {
      return false;
    }

    // DisplayOnly product without user pricelist - hide price
    return true;
  };

  return {
    canPurchase,
    isOnUserPricelist,
    shouldShowDisplayOnlyPrice,
  };
}
